import { z } from 'zod'

export const MasteryLevelSchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
])

export const LearningProgressSchema = z.object({
  knowledgeId: z.string().min(1),
  masteryLevel: MasteryLevelSchema,
  studyCount: z.number().int().nonnegative(),
  lastStudyAt: z.string().datetime().optional(),
  nextReviewAt: z.string().datetime().optional(),
  favorite: z.boolean(),
  note: z.string().optional(),
  wrongCount: z.number().int().nonnegative(),
})

export const WrongQuestionRecordSchema = z.object({
  questionId: z.string().min(1),
  knowledgeId: z.string().min(1),
  selectedAnswer: z.array(z.string()),
  wrongCount: z.number().int().positive(),
  lastWrongAt: z.string().datetime(),
  mastered: z.boolean(),
})

export const UserSettingsSchema = z.object({
  dailyNewKnowledge: z.number().int().min(1).max(100),
  maxDailyReviews: z.number().int().min(1).max(500),
  defaultCategory: z.string().nullable(),
  randomOrder: z.boolean(),
  showMastered: z.boolean(),
  themeMode: z.enum(['light', 'dark', 'system']),
})

export const DailyStatisticSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  learned: z.number().int().nonnegative(),
  reviewed: z.number().int().nonnegative(),
  answered: z.number().int().nonnegative(),
  correct: z.number().int().nonnegative(),
})

export const PracticeModeSchema = z.enum(['all', 'random', 'category', 'wrong', 'retry'])

export const QuestionAttemptSchema = z.object({
  id: z.string().min(1),
  questionId: z.string().min(1),
  knowledgeId: z.string().min(1),
  sessionId: z.string().min(1),
  answeredAt: z.string().datetime(),
  userAnswer: z.array(z.string()),
  correctAnswer: z.array(z.string()),
  isCorrect: z.boolean(),
  mode: PracticeModeSchema,
  category: z.string().min(1).optional(),
  durationMs: z.number().int().nonnegative().optional(),
  contentVersion: z.string().min(1),
})

export const PracticeSessionSchema = z
  .object({
    id: z.string().min(1),
    startedAt: z.string().datetime(),
    finishedAt: z.string().datetime().optional(),
    mode: PracticeModeSchema,
    category: z.string().min(1).optional(),
    questionIds: z.array(z.string().min(1)),
    attemptIds: z.array(z.string().min(1)),
    totalCount: z.number().int().nonnegative(),
    answeredCount: z.number().int().nonnegative(),
    correctCount: z.number().int().nonnegative(),
    wrongCount: z.number().int().nonnegative(),
  })
  .superRefine((session, context) => {
    if (new Set(session.questionIds).size !== session.questionIds.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['questionIds'],
        message: '题目 ID 不能重复。',
      })
    }
    if (new Set(session.attemptIds).size !== session.attemptIds.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['attemptIds'],
        message: '答题记录 ID 不能重复。',
      })
    }
    if (session.totalCount !== session.questionIds.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['totalCount'],
        message: '总题数必须与题目 ID 数量一致。',
      })
    }
    if (session.answeredCount !== session.attemptIds.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['answeredCount'],
        message: '已答数必须与答题记录 ID 数量一致。',
      })
    }
    if (session.correctCount + session.wrongCount !== session.answeredCount) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['correctCount'],
        message: '正确数与错误数之和必须等于已答数。',
      })
    }
    if (session.answeredCount > session.totalCount) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['answeredCount'],
        message: '已答数不能超过总题数。',
      })
    }
  })

export const ContentMigrationSchema = z.object({
  fromContentVersion: z.string(),
  toContentVersion: z.string(),
  migratedAt: z.string().datetime(),
  preservedProgressCount: z.number().int().nonnegative(),
  preservedWrongQuestionCount: z.number().int().nonnegative(),
  preservedQuestionAttemptCount: z.number().int().nonnegative().default(0),
  preservedPracticeSessionCount: z.number().int().nonnegative().default(0),
  orphanProgressIds: z.array(z.string()).default([]),
  orphanWrongQuestionIds: z.array(z.string()).default([]),
  orphanQuestionAttemptIds: z.array(z.string()).default([]),
  orphanPracticeSessionIds: z.array(z.string()).default([]),
})

const UserDatabaseV1Fields = {
  schemaVersion: z.literal(1),
  contentVersion: z.string().min(1),
  updatedAt: z.string().datetime(),
  progress: z.record(LearningProgressSchema),
  wrongQuestions: z.record(WrongQuestionRecordSchema),
  settings: UserSettingsSchema,
  dailyStatistics: z.array(DailyStatisticSchema),
  recentKnowledgeId: z.string().min(1).optional(),
  contentMigration: ContentMigrationSchema.optional(),
}

export const UserDatabaseV1Schema = z.object(UserDatabaseV1Fields)

export const UserDatabaseSchema = z.object({
  ...UserDatabaseV1Fields,
  schemaVersion: z.literal(2),
  questionAttempts: z.record(QuestionAttemptSchema),
  practiceSessions: z.record(PracticeSessionSchema),
})

export const UserDataExportV1Schema = z.object({
  schemaVersion: z.literal(1),
  exportedAt: z.string().datetime(),
  data: UserDatabaseV1Schema,
})

export const UserDataExportSchema = z.object({
  schemaVersion: z.literal(2),
  exportedAt: z.string().datetime(),
  data: UserDatabaseSchema,
})

export type MasteryLevel = z.infer<typeof MasteryLevelSchema>
export type LearningProgress = z.infer<typeof LearningProgressSchema>
export type WrongQuestionRecord = z.infer<typeof WrongQuestionRecordSchema>
export type UserSettings = z.infer<typeof UserSettingsSchema>
export type DailyStatistic = z.infer<typeof DailyStatisticSchema>
export type PracticeMode = z.infer<typeof PracticeModeSchema>
export type QuestionAttempt = z.infer<typeof QuestionAttemptSchema>
export type PracticeSession = z.infer<typeof PracticeSessionSchema>
export type ContentMigration = z.infer<typeof ContentMigrationSchema>
export type UserDatabaseV1 = z.infer<typeof UserDatabaseV1Schema>
export type UserDatabase = z.infer<typeof UserDatabaseSchema>
export type UserDataExport = z.infer<typeof UserDataExportSchema>
